import * as React from 'react'
import styled from 'styled-typed'
import { IconTextInput, SingleSelectOption } from 'uiComponents/input'
import { faSearch } from '@fortawesome/free-solid-svg-icons'
import { faSlidersH } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { withNavigation } from 'hocs'
import { Navigation } from 'navigation'
import { match as RouteMatch } from 'react-router-dom'
import { SingleSelect } from 'uiComponents/input'
import { ActionButton } from 'uiComponents/buttons'
import { MessageKind } from 'uiComponents/messages'
import { delay } from 'utils'

const Container = styled.div`
    background: ${(props) => props.theme.colors.border};
    border-radius: 4px 4px 0px 0px;
    padding: 1em;
    display: flex;
`
const SearchField = styled(IconTextInput)`
    background: ${(props) => props.theme.colors.white};
    border: 1px solid ${(props) => props.theme.colors.border};
    border-radius: 4px 0 0 4px;
    font-size: 0.875em;
    height: 3rem;
    padding-left: 3em !important;
    font-weight: normal;
    flex: 1;

    &:focus {
        border: 1px solid ${(props) => props.theme.colors.border};
    }
`
const SearchOptions = styled.div`
    border-radius: 0 4px 4px 0;
    background: ${(props) => props.theme.colors.white};
    border: 1px solid ${(props) => props.theme.colors.border};
    display: flex;
    align-items: center;
`
const Icon = styled(FontAwesomeIcon)`
    color: ${(props) => props.theme.colors.boyBlue};
    margin-left: 1.2em;
`

interface SearchBarProps {
    searchByOptions: SingleSelectOption[]
    defaultSearchBy?: string
    navigation: Navigation
    match: RouteMatch<any>
    minSearchLength?: number
    maxSearchLength?: number
    selectWidth?: string
    searchOnEnterPaths?: string[]
    onSearch?: () => void
    replaceMessages: (id: string, status: MessageKind, text: string) => void
    removeAllMessages: () => void
}

function SearchBar(props: SearchBarProps) {
    const [search, setSearch] = React.useState<string>('')
    const [searchBy, setSearchBy] = React.useState<string>('')

    const setSearchParameters = () => {
        const query = props.navigation.query()
        const searchText = query.search || ''
        const searchByCategory = query.searchBy || props.defaultSearchBy || ''
        setSearch(searchText)
        setSearchBy(searchByCategory)
    }

    React.useEffect(() => {
        setSearchParameters()
    }, [])

    React.useEffect(() => {
        document.addEventListener('keydown', onEnter)
        return () => document.removeEventListener('keydown', onEnter)
    }, [search, searchBy])

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearch(e.target.value)
    }

    const handleSearchByChange = (value: string) => {
        setSearchBy(value)
    }

    const onEnter = (e: KeyboardEvent) => {
        const searchOnEnterAllowed =
            props.searchOnEnterPaths && !!props.searchOnEnterPaths.find((p) => location.pathname.indexOf(p) > -1)
        if (searchOnEnterAllowed && e.keyCode === 13) {
            onSearch()
        }
    }

    const onSearch = async () => {
        if (props.minSearchLength && search.length < props.minSearchLength) {
            await flashErrorMessage(`Minimum search length is ${props.minSearchLength} symbols`)
        } else if (props.maxSearchLength && search.length > props.maxSearchLength) {
            await flashErrorMessage(`Maximum search length is ${props.maxSearchLength} symbols`)
        } else {
            props.navigation.addQueryWithReplace({ search, searchBy })
            if (props.onSearch) {
                props.onSearch()
            }
        }
    }

    const flashErrorMessage = async (text: string) => {
        props.replaceMessages('error', 'error', text)
        await delay(4000)
        props.removeAllMessages()
    }

    return (
        <Container>
            <SearchField
                id="search-text"
                iconLeft={faSearch}
                placeholder="Enter search text"
                value={search}
                onChange={handleSearchChange}
                onFinishTyping={(v) => setSearch(v.trim())}
                block
            />
            <SearchOptions>
                <Icon icon={faSlidersH} />
                <SingleSelect
                    id="search-by"
                    options={props.searchByOptions}
                    noSelectOption="Select search by"
                    width={props.selectWidth || '11em'}
                    selected={searchBy}
                    onSelect={handleSearchByChange}
                    whiteBackground
                    style={{ margin: '0 .5em' }}
                />
                <ActionButton
                    id="search-button"
                    kind="action"
                    size="large"
                    style={{ marginRight: '.2em' }}
                    onClick={onSearch}
                >
                    Search
                </ActionButton>
            </SearchOptions>
        </Container>
    )
}

export default withNavigation(SearchBar)
